/**
 * Notification system type definitions.
 *
 * These types define the canonical notification registry shared
 * across provider-app and patient-app.
 */

export const NotificationChannel = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  IN_APP: 'IN_APP',
} as const;

export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const NotificationCategory = {
  ACCOUNT: 'ACCOUNT',
  SYSTEM: 'SYSTEM',
  HSAT: 'HSAT',
  DME: 'DME',
  PAYMENT: 'PAYMENT',
  REMINDER: 'REMINDER',
  TROUBLESHOOTING: 'TROUBLESHOOTING',
} as const;

export type NotificationCategory = (typeof NotificationCategory)[keyof typeof NotificationCategory];

export const NotificationAudience = {
  PATIENT: 'PATIENT',
  PROVIDER: 'PROVIDER',
} as const;

export type NotificationAudience = (typeof NotificationAudience)[keyof typeof NotificationAudience];

export const ProviderRole = {
  CARE_COORDINATOR: 'CARE_COORDINATOR',
  NP: 'NP',
  MD: 'MD',
} as const;

export type ProviderRole = (typeof ProviderRole)[keyof typeof ProviderRole];

/**
 * Maps notification ProviderRole enum values to FHIR ProviderRoleCode strings
 * used in PractitionerRole.code coding.
 */
export const PROVIDER_ROLE_TO_FHIR: Record<ProviderRole, string> = {
  [ProviderRole.CARE_COORDINATOR]: 'care-coordinator',
  [ProviderRole.NP]: 'nurse-practitioner',
  [ProviderRole.MD]: 'physician',
} as const;

export interface NotificationTypeDefinition {
  /** Dot-separated key: `audience.category.name` */
  key: string;
  /** Reference code from the notification matrix CSV (e.g. Account_00, HSAT_04) */
  referenceCode: string;
  /** Human-readable label */
  label: string;
  /** Which audience receives this notification */
  audience: NotificationAudience;
  /** Notification category */
  category: NotificationCategory;
  /** Channels this notification can be delivered through */
  channels: NotificationChannel[];
  /** Provider roles that receive this notification (provider notifications only) */
  recipientRoles?: ProviderRole[];
  /** Short description of the notification purpose */
  description: string;
  /** Default delivery intervals (e.g. ["immediately", "24h-before", "15m-before"]) */
  defaultIntervals?: string[];
  /** Number of delivery attempts for multi-attempt notifications */
  maxAttempts?: number;
  /**
   * Bypass `consent-to-contact` check. Reserve for HIPAA-essential messages:
   * account security, transactional receipts. Defaults to false.
   */
  essential?: boolean;
}
